import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { SportsManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Sport } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Sports/PathItems';
import EditSport from './EditSport';
import AISuggestions from '../../components/AISuggestions';

function Sports() {
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [filteredSports, setFilteredSports] = useState<Sport[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSportName, setNewSportName] = useState('');
  const [newSportTranslationKey, setNewSportTranslationKey] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sportToDelete, setSportToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingSport, setEditingSport] = useState<string | null>(null);

  const searchSports = async (query: string) => {
    onError((await Session.getClient().request(SportsManagement.searchSport({ query, limit: 100, offset: 0 })))
      .maybeOk((value) => setFilteredSports(value as Sport[])));
  };

  useEffect(() => {
    searchSports('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddSport = () => {
    setNewSportName('');
    setNewSportTranslationKey('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewSportName('');
    setNewSportTranslationKey('');
  };

  const handleDialogOk = async () => {
    if (newSportName.trim()) {
      onError((await Session.getClient().request(SportsManagement.createSport({ name: newSportName.trim() })))
        .maybeOk(() => searchSports(search)));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setSportToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSportToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (sportToDelete) {
      onError((await Session.getClient().addPathParam('sportId', sportToDelete.id).request(SportsManagement.deleteSport()))
        .maybeOk(() => searchSports(search)));
    }
    handleDeleteDialogClose();
  };

  if (editingSport) {
    return <EditSport id={editingSport} onBack={() => {
      searchSports(search);
      setEditingSport(null);
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          label="Search sports"
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchSports(e.target.value);
          }}
          sx={{ width: 250 }}
        />
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(filteredSports)}
          usageInstructions={SportsManagement.toAgentInstructions()}
          developerMessage={'The user is in the view where they can search and see a list of sports. For any of the operations ' +
            'regarding a specific sport the path parameter with the name sportId MUST be specified with that sport id.'}
          onOperationsApplied={() => searchSports(search)}
        />
        <Button variant="contained" color="primary" onClick={handleAddSport}>Create Sport</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Translation Key</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSports.map((sport) => (
              <TableRow key={sport.id}>
                <TableCell>{sport.name}</TableCell>
                <TableCell>{sport.translationKey}</TableCell>
                <TableCell>
                  <Switch checked={sport.state.state === 'published'} disabled />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => setEditingSport(sport.id)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(sport.id, sport.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create Sport</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Sport Name"
            fullWidth
            autoFocus
            value={newSportName}
            onChange={e => setNewSportName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Translation Key"
            fullWidth
            value={newSportTranslationKey}
            onChange={e => setNewSportTranslationKey(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Sport</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the sport "{sportToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sports;
